/**
 * Revalidate + Reinstall icon buttons for the GHL Qualifier location cards.
 *
 * Cloned from `components/ghl-dashboard/TokenActions.tsx`. The clone preserves
 * the original (which targets the Middleware) and lets this version aim at
 * the Qualifier's OAuth flow + token-refresh endpoint instead. The two now
 * genuinely differ in which OAuth flow they open, so a shared component with
 * a "provider" prop would just shuffle the discriminator into the call site.
 *
 * Behavioral parity with TokenActions:
 *   - Both buttons open an AlertDialog confirmation before acting.
 *   - Revalidate posts to `${qualifierUrl}/health/auth/refresh/:locationId`
 *     via `useRevalidateQualifierToken`.
 *   - Reinstall opens `${qualifierUrl}/oauth/authorize` in a new tab.
 *   - Both buttons hide when `qualifierUrl` is null (degraded state).
 */

import { useState } from 'react'
import { RefreshCw, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  useRevalidateQualifierToken,
  RevalidateError,
  type TokenStatus,
} from '@/hooks/useQualifierTokenHealth'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Props {
  readonly locationId: string
  readonly tokenStatus: TokenStatus
  readonly qualifierUrl: string | null
  /** icon-only: compact for list cards; with-label: full text for detail header */
  readonly variant?: 'icon-only' | 'with-label'
}

type ConfirmAction = 'revalidate' | 'reinstall' | null

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function QualifierTokenActions({
  locationId,
  qualifierUrl,
  variant = 'icon-only',
}: Props) {
  const revalidate = useRevalidateQualifierToken()
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null)

  function doRevalidate() {
    setErrorMsg(null)
    revalidate.mutate(locationId, {
      onError: (err) => {
        if (err instanceof RevalidateError && err.requiresReinstall) {
          setErrorMsg('Reinstall required')
        } else {
          setErrorMsg('Refresh failed')
        }
      },
      onSuccess: () => {
        setErrorMsg(null)
      },
    })
  }

  function doReinstall() {
    if (!qualifierUrl) return
    // Click handler runs inside a user gesture so popup blockers permit
    // window.open. New-tab preserves the dashboard while the operator
    // walks through GHL's consent screen.
    window.open(`${qualifierUrl}/oauth/authorize`, '_blank', 'noopener,noreferrer')
  }

  function handleConfirm() {
    if (confirmAction === 'revalidate') doRevalidate()
    else if (confirmAction === 'reinstall') doReinstall()
    setConfirmAction(null)
  }

  const isPending = revalidate.isPending

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      {errorMsg && (
        <span className="text-[10px] text-red-500 font-medium whitespace-nowrap">
          {errorMsg}
        </span>
      )}

      {/* Revalidate button — only available when a Qualifier URL is configured */}
      {qualifierUrl && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-1.5 text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation()
                  setConfirmAction('revalidate')
                }}
                disabled={isPending}
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isPending ? 'animate-spin' : ''}`} />
                {variant === 'with-label' && (
                  <span className="ml-1.5 text-xs">Revalidate</span>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Force-refresh this location&apos;s Qualifier OAuth token
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Reinstall button — opens the Qualifier OAuth flow (NOT the Middleware's) */}
      {qualifierUrl && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-1.5 text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation()
                  setConfirmAction('reinstall')
                }}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {variant === 'with-label' && (
                  <span className="ml-1.5 text-xs">Reinstall</span>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Re-authorize the Qualifier app for this location (opens GHL consent screen)
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null)
        }}
      >
        <AlertDialogContent>
          {confirmAction === 'revalidate' ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Revalidate Qualifier OAuth token?</AlertDialogTitle>
                <AlertDialogDescription>
                  This force-refreshes the GoHighLevel OAuth token used by the
                  GHL Qualifier app for this location by exchanging the stored
                  refresh token for a new access token.
                  <br />
                  <br />
                  If GHL rejects the refresh token (e.g., the Qualifier app was
                  uninstalled remotely), you&apos;ll see a &quot;Reinstall required&quot; error
                  and need to re-authorize via the Reinstall button.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleConfirm}>
                  Revalidate
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : confirmAction === 'reinstall' ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Reinstall the Qualifier app for this location?</AlertDialogTitle>
                <AlertDialogDescription>
                  You&apos;ll be redirected to GoHighLevel&apos;s consent screen in a new
                  tab. After you authorize, the stored Qualifier tokens for
                  this location will be replaced with fresh ones.
                  <br />
                  <br />
                  Cron-driven Claude classifications using the current token
                  may briefly fail until the new token is saved. Only reinstall
                  if Revalidate didn&apos;t resolve the issue.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleConfirm}>
                  Continue to GHL
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : null}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
